import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./BookingCreate.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { TextField, Tabs, Tab } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { GoogleMap, LoadScript, Marker, DirectionsService, DirectionsRenderer } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { useNavigate } from "react-router-dom";
import { fetchVehiclesData } from "../../../store/vehicles/actions";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BookingCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSchedule, setIsSchedule] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loadingBtn, setLoadingBtn] = useState(false);

  const [time, setTime] = useState(new Date());

  const [locationTab, setLocationTab] = useState(0);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [directionsResponse, setDirectionsResponse] = useState(null);

  const { data, loading } = useSelector((state) => state.Vehicles);

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  useEffect(() => {
    dispatch(fetchVehiclesData());
  }, [dispatch]);

  useEffect(() => {
    if (!loading) {
      setVehicles(data?.data || []);
    }
  }, [data, loading]);

  const nextStep = () => {
    if (step === 1 && !selectedVehicle) {
      toast.error("Please select a vehicle!");
      return;
    }

    if (step === 2 && !time) {
      toast.error("Please select pickup time!");
      return;
    }

    if (step === 2 && isSchedule) {
      const selectedDays = weekDays.filter(day => document.getElementById(day).checked);
      if (selectedDays.length === 0) {
        toast.error("Please select at least one day for scheduled trip!");
        return;
      }
    }

    if (step === 3 && (!pickup || !dropoff)) {
      toast.error("Please select both pickup and dropoff locations!");
      return;
    }

    setStep(prev => prev + 1);
  };
  const prevStep = () => setStep((prev) => prev - 1);

  const SearchBox = ({ setLocation }) => {
    const {
      ready,
      value,
      suggestions: { status, data },
      setValue,
      clearSuggestions,
    } = usePlacesAutocomplete();

    const handleSelect = async (description) => {
      setValue(description, false);
      clearSuggestions();

      try {
        const results = await getGeocode({ address: description });
        const { lat, lng } = await getLatLng(results[0]);
        setLocation({ lat, lng });

        if (locationTab === 0) setPickupAddress(description);
        else setDropoffAddress(description);
      } catch (error) {
        console.error("Error:", error);
      }
    };

    return (
      <div className="autocomplete-container">
        <input
          className="form-control mb-2"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!ready}
          placeholder="Search location..."
        />
        {status === "OK" && (
          <ul className="list-group">
            {data.map(({ place_id, description }) => (
              <li
                key={place_id}
                className="list-group-item list-group-item-action"
                onClick={() => handleSelect(description)}
                style={{ cursor: "pointer" }}
              >
                {description}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const handleCreateBooking = async () => {
    try {
      if (!pickup || !dropoff) {
        toast.error("Pickup and Dropoff locations are required!");
        return;
      }

      setLoadingBtn(true);

      const payload = {
        source_address: pickupAddress,
        dest_address: dropoffAddress,
        source_lat: pickup.lat,
        source_long: pickup.lng,
        dest_lat: dropoff.lat,
        dest_long: dropoff.lng,
        vehicle_id: selectedVehicle,
        total_kms:
          directionsResponse?.routes?.[0]?.legs?.[0]?.distance?.text || "0",
        payment_method: "cash",
        journey_date: new Date().toISOString().split("T")[0],
        journey_time: time.toLocaleTimeString("en-GB", { hour12: false }),
      };

      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${process.env.REACT_APP_WEB_APP_URL}/api/book-now`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success("🎉 Booking Created Successfully!");
        setTimeout(() => {
          navigate("/profile");
        }, 1000);
      } else {
        toast.error(response.data.message || "Failed to create booking");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while creating booking!");
    } finally {
      setLoadingBtn(false);
    }
  };

  document.title = "Create Booking | Sawari Online";

  return (
    <div className="container py-5 booking-wrapper">
      {/* Header */}
      <div className="text-center mb-5">
        <h2 className="fw-bold text-gradient">🚗 Create Booking</h2>
        <p className="text-muted">Follow the steps to schedule your trip</p>
      </div>

      {/* Stepper */}
      <div className="stepper-wrapper mb-5">
        {["Vehicle", "Booking Time", "Locations", "Details"].map(
          (label, index) => {
            const current = index + 1;
            return (
              <div
                key={label}
                className={`stepper-item ${step === current ? "active" : ""} ${step > current ? "completed" : ""
                  }`}
              >
                <div className="step-counter">{current}</div>
                <div className="step-name">{label}</div>
              </div>
            );
          }
        )}
      </div>

      {/* Step 1: Vehicle */}
      {step === 1 && (
        <div className="card custom-card">
          <div className="card-header">Select Vehicle</div>
          <div className="card-body">
            <div className="row g-4">
              {vehicles.map((v) => (
                <>
                  {v.in_service == 1 ? (
                    <div key={v.id} className="col-md-4">
                      <div
                        className={`vehicle-card 
                          ${selectedVehicle === v.id ? "selected" : ""} 
                          ${v.is_enable === 0 ? "disabled-card" : ""}`
                        }
                        onClick={() => v.is_enable !== 0 && setSelectedVehicle(v.id)}
                        style={{ cursor: v.is_enable === 0 ? "not-allowed" : "pointer" }}
                      >
                        {v.vehicle_media.length > 1 ? (
                          <div className="vehicle-icon">
                            <img src={v.vehicle_media[0].url} height={100} width={100} />
                          </div>
                        ) : (
                          <div className="vehicle-icon">🚘</div>
                        )}
                        <h6 className="fw-bold">{v.make_name}</h6>
                        <h6 className="fw-bold">{v.license_plate}</h6>
                        <p className="text-muted mb-2">{v.seats} Seats</p>
                        <p className="text-muted mb-2">Rate Per Seat: PKR {v.seat_rate}</p>
                      </div>
                    </div>
                  ) : null}
                </>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Schedule */}
      {step === 2 && (
        <div className="card schedule-card shadow-sm">
          <div className="card-header">⏰ Pickup Time</div>
          <div className="card-body">

            {/* Time aur Schedule row */}
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1 me-3">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    ampm
                    label="Select Pickup Time"
                    value={time}
                    onChange={(newValue) => setTime(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: "bold",
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </div>

              {/* Schedule Trip Toggle */}
              <div className="form-check form-switch d-flex align-items-center">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="scheduleCheck"
                  checked={isSchedule}
                  onChange={() => setIsSchedule(!isSchedule)}
                />
                <label htmlFor="scheduleCheck" className="form-check-label fw-semibold ms-2">
                  Schedule Trip?
                </label>
              </div>
            </div>

            {isSchedule && (
              <div className="col-12 mt-4 text-center">
                <label className="form-label fw-semibold">Select Days</label>
                <div className="weekdays-wrapper d-flex justify-content-center flex-wrap gap-2 mt-2">
                  {weekDays.map((day) => (
                    <div key={day} className="weekday-chip">
                      <input
                        type="checkbox"
                        className="btn-check"
                        id={day}
                        autoComplete="off"
                      />
                      <label htmlFor={day} className="btn btn-sm btn-outline-custom">
                        {day}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card custom-card">
          <div className="card-header">📍 Select Locations</div>
          <div className="card-body">
            {/* Tabs */}
            <Tabs
              value={locationTab}
              onChange={(e, newVal) => setLocationTab(newVal)}
              centered
            >
              <Tab label="Pickup" />
              <Tab label="Dropoff" />
            </Tabs>

            {/* Searchbar */}
            <div className="my-3">
              {locationTab === 0 ? (
                <SearchBox setLocation={setPickup} />
              ) : (
                <SearchBox setLocation={setDropoff} />
              )}
            </div>

            <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY} libraries={["places"]}>
              <GoogleMap
                center={pickup || dropoff || { lat: 24.8607, lng: 67.0011 }}
                zoom={12}
                mapContainerStyle={{ width: "100%", height: "400px", borderRadius: "12px" }}
                onClick={(e) => {
                  const clicked = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                  if (locationTab === 0) {
                    setPickup(clicked);
                  } else {
                    setDropoff(clicked);
                  }
                }}
              >
                {pickup && (
                  <Marker
                    position={pickup}
                    icon={{
                      url:
                        "data:image/svg+xml;charset=UTF-8," +
                        encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50">
                <circle cx="25" cy="25" r="22" fill="#4CAF50" stroke="white" stroke-width="3"/>
                <text x="50%" y="55%" text-anchor="middle" fill="white" font-size="18" font-weight="bold">P</text>
              </svg>
            `),
                      scaledSize: new window.google.maps.Size(45, 45),
                    }}
                  />
                )}

                {/* Dropoff Marker */}
                {dropoff && (
                  <Marker
                    position={dropoff}
                    icon={{
                      url:
                        "data:image/svg+xml;charset=UTF-8," +
                        encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50">
                <circle cx="25" cy="25" r="22" fill="#F44336" stroke="white" stroke-width="3"/>
                <text x="50%" y="55%" text-anchor="middle" fill="white" font-size="18" font-weight="bold">D</text>
              </svg>
            `),
                      scaledSize: new window.google.maps.Size(45, 45),
                    }}
                  />
                )}

                {pickup && dropoff && (
                  <DirectionsService
                    options={{
                      destination: dropoff,
                      origin: pickup,
                      travelMode: "DRIVING",
                    }}
                    callback={(res) => {
                      if (res !== null && res.status === "OK") {
                        setDirectionsResponse(res);
                      }
                    }}
                  />
                )}

                {directionsResponse && (
                  <DirectionsRenderer
                    options={{
                      directions: directionsResponse,
                      polylineOptions: {
                        strokeColor: "#6C63FF",
                        strokeOpacity: 0.9,
                        strokeWeight: 5,
                      },
                      suppressMarkers: true,
                    }}
                  />
                )}
              </GoogleMap>
            </LoadScript>

            <div className="mt-3 text-center">
              {pickupAddress && <p><strong>Pickup:</strong> {pickupAddress}</p>}
              {dropoffAddress && <p><strong>Dropoff:</strong> {dropoffAddress}</p>}
            </div>

          </div>
        </div>
      )}

      {step === 4 && (
        <div className="card custom-card shadow">
          <div className="card-header">📑 Booking Summary</div>
          <div className="card-body">
            {/* Vehicle */}
            <h6 className="fw-bold">🚘 Vehicle</h6>
            {selectedVehicle ? (
              <p className="text-muted">
                {vehicles.find((v) => v.id === selectedVehicle)?.make_name} (
                {vehicles.find((v) => v.id === selectedVehicle)?.seats} Seats)
              </p>
            ) : (
              <p className="text-danger">No vehicle selected</p>
            )}

            <hr />

            {/* Schedule */}
            <h6 className="fw-bold">⏰ Pickup Time</h6>
            <p className="text-muted">{time.toLocaleTimeString()}</p>
            {isSchedule && <p className="badge bg-info">Scheduled Trip</p>}

            <hr />

            {/* Locations */}
            <h6 className="fw-bold">📍 Locations</h6>
            <p>
              <span className="fw-semibold">Pickup:</span>{" "}
              {pickupAddress || "Not selected"}
            </p>
            <p>
              <span className="fw-semibold">Dropoff:</span>{" "}
              {dropoffAddress || "Not selected"}
            </p>

            {pickup && dropoff && (
              <div className="map-preview mt-3">
                <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}>
                  <GoogleMap
                    center={pickup}
                    zoom={12}
                    mapContainerStyle={{ width: "100%", height: "250px", borderRadius: "12px" }}
                  >
                    <Marker position={pickup} />
                    <Marker position={dropoff} />
                    {directionsResponse && (
                      <DirectionsRenderer
                        options={{
                          directions: directionsResponse,
                          polylineOptions: {
                            strokeColor: "#6C63FF",
                            strokeOpacity: 0.9,
                            strokeWeight: 5,
                          },
                          suppressMarkers: false,
                        }}
                      />
                    )}
                  </GoogleMap>
                </LoadScript>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="d-flex justify-content-between mt-4">
        {step > 1 ? (
          <button className="btn btn-outline-secondary px-4" onClick={prevStep}>
            ← Back
          </button>
        ) : (
          <div></div>
        )}

        {step < 4 ? (
          <button
            className="btn btn-gradient px-5 py-2"
            onClick={nextStep}
            disabled={!selectedVehicle}
          >
            Next →
          </button>
        ) : (
          <button
            className="btn btn-gradient px-5 py-2 fw-bold"
            onClick={handleCreateBooking}
            disabled={loadingBtn}
          >
            {loadingBtn ? (
              <span>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Creating...
              </span>
            ) : (
              "Create Booking"
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingCreate;
